/**
 * Studio Services — the intake form's vocabulary, shared by api + web.
 *
 * Every choice the form offers is an `{ id, label }` pair defined ONCE here.
 * The web renders the labels; the api validates against the ids. Keeping both
 * in one module is the point: an option added to the form but not to the
 * validator (or the other way round) would reject real leads with a 400, and
 * that failure is invisible until someone notices the ads stopped converting.
 *
 * Zero dependencies, so the web bundle pays only for the strings.
 */
/** The ids of an option list, as a tuple zod's `z.enum` accepts. */
export function idsOf(options) {
    const ids = options.map((o) => o.id);
    if (ids.length === 0)
        throw new Error('idsOf: empty option list');
    return ids;
}
/** Label for an id, or the id itself for an unknown one (never throws). */
export function labelOf(options, id) {
    if (!id)
        return '—';
    return options.find((o) => o.id === id)?.label ?? id;
}
export const CATEGORIES = [
    {
        id: 'web_app',
        label: 'Web App',
        blurb: 'Works on mobile and desktop. Logins, dashboards, bookings, payments.',
        fromCentavos: 1_000_000,
        param: 'web-app',
    },
    {
        id: 'web_opt',
        label: 'Web App Optimization',
        blurb: 'Already have a site or app? Make it faster, fix what’s broken, add what’s missing.',
        fromCentavos: 1_000_000,
        param: 'optimization',
    },
    {
        id: 'ai_agent',
        label: 'AI Agent',
        blurb: 'An assistant your team messages on Slack, Telegram or Discord. It reads your inbox, answers from your files, and does the routine work.',
        fromCentavos: 500_000,
        monthlyFromCentavos: 200_000,
        param: 'ai-agent',
    },
    {
        id: 'rsvp',
        label: 'RSVP & Event Site',
        blurb: 'Invitation, RSVP, guest list, venue map, and a story deck.',
        fromCentavos: 500_000,
        param: 'rsvp',
    },
    {
        id: 'personal',
        label: 'Personal Website / Blog',
        blurb: 'Portfolio, blog, resume, or a link page on your own name.',
        fromCentavos: 500_000,
        param: 'personal',
    },
    {
        id: 'logo',
        label: 'Logo Design',
        blurb: 'A logo for your business in 3–5 options, plus an animated version and your social media profile kit.',
        fromCentavos: 500_000,
        param: 'logo',
    },
    {
        id: 'custom',
        label: 'Something else',
        blurb: 'Tell us what you have in mind.',
        fromCentavos: null,
        param: 'custom',
    },
];
/** `?type=rsvp` → 'rsvp'. Unknown or absent → null (no card preselected). */
export function categoryFromParam(param) {
    if (!param)
        return null;
    const p = param.trim().toLowerCase();
    return CATEGORIES.find((c) => c.param === p || c.id === p)?.id ?? null;
}
export const TIMELINES = [
    { id: 'asap', label: 'ASAP (under 2 weeks)' },
    { id: 'month', label: 'Within a month' },
    { id: 'quarter', label: '1–3 months' },
    { id: 'flexible', label: 'Flexible' },
];
export const BUDGETS = [
    { id: '5-10k', label: '₱5k – ₱10k', floorCentavos: 500_000 },
    { id: '10-25k', label: '₱10k – ₱25k', floorCentavos: 1_000_000 },
    { id: '25-50k', label: '₱25k – ₱50k', floorCentavos: 2_500_000 },
    { id: '50k+', label: '₱50k and up', floorCentavos: 5_000_000 },
    { id: 'unsure', label: 'Not sure yet', floorCentavos: 0 },
];
/**
 * The budget ranges worth offering for a category: none that top out below its
 * "starts at" price. A Web App card that says "from ₱10,000" and then offers
 * "₱5k – ₱10k" is asking for a lead we'd have to turn down.
 */
export function budgetsFor(category) {
    const from = CATEGORIES.find((c) => c.id === category)?.fromCentavos ?? 0;
    return BUDGETS.filter((b) => b.id === 'unsure' || nextFloor(b.id) > from);
}
function nextFloor(id) {
    const i = BUDGETS.findIndex((b) => b.id === id);
    const next = BUDGETS.slice(i + 1).find((b) => b.id !== 'unsure');
    return next ? next.floorCentavos : Number.POSITIVE_INFINITY;
}
/**
 * What a lead is worth, in whole pesos, for GA4's `value` and Meta's.
 *
 * The floor of the band the visitor picked — never its ceiling, which nobody
 * told us. "Not sure yet" has no floor, so the category's own starting price
 * stands in; "Something else" has neither and reports nothing, which is
 * honest: an unpriced request has no value we can claim.
 *
 * Whole pesos because both platforms read `value` in the major unit — send the
 * centavo figure and a ₱10,000 lead is reported as ₱1,000,000. It lives here,
 * not in the browser, so the pixel event and its server copy cannot disagree.
 */
export function leadValuePesos(category, budget) {
    const floor = BUDGETS.find((b) => b.id === budget)?.floorCentavos ?? 0;
    const centavos = floor > 0 ? floor : (CATEGORIES.find((c) => c.id === category)?.fromCentavos ?? 0);
    return Math.round(centavos / 100);
}
export const HAS_DOMAIN = [
    { id: 'yes', label: 'Yes' },
    { id: 'help', label: 'No, help me get one' },
    { id: 'unsure', label: 'Not sure' },
];
export const WEB_APP_USERS = [
    { id: 'me', label: 'Just me' },
    { id: 'team', label: 'My team' },
    { id: 'customers', label: 'My customers' },
    { id: 'public', label: 'The public' },
];
export const WEB_APP_FEATURES = [
    { id: 'accounts', label: 'Logins & accounts' },
    { id: 'payments', label: 'Online payments (GCash / Maya / card)' },
    { id: 'admin', label: 'Admin dashboard' },
    { id: 'booking', label: 'Booking / scheduling' },
    { id: 'records', label: 'Records / inventory' },
    { id: 'notifications', label: 'Email or SMS notifications' },
    { id: 'reports', label: 'Reports & export' },
    { id: 'uploads', label: 'File uploads' },
    { id: 'unsure', label: 'Not sure yet' },
];
export const WEB_APP_CURRENT = [
    { id: 'paper', label: 'Paper' },
    { id: 'spreadsheet', label: 'Spreadsheet' },
    { id: 'other_app', label: 'Another app' },
    { id: 'nothing', label: 'Nothing yet' },
];
export const USER_COUNTS = [
    { id: 'lt50', label: 'Under 50' },
    { id: '50-500', label: '50 – 500' },
    { id: '500+', label: '500+' },
    { id: 'unsure', label: 'Not sure' },
];
export const OPTIMIZE_GOALS = [
    { id: 'speed', label: 'Make it faster' },
    { id: 'mobile', label: 'Fix it on phones' },
    { id: 'design', label: 'Refresh the design' },
    { id: 'seo', label: 'Show up on Google' },
    { id: 'bugs', label: 'Fix what’s broken' },
    { id: 'features', label: 'Add features' },
    { id: 'security', label: 'Make it secure' },
    { id: 'hosting', label: 'Cut hosting costs' },
    { id: 'unsure', label: 'Not sure yet' },
];
export const SITE_PLATFORMS = [
    { id: 'shopify', label: 'Shopify' },
    { id: 'builder', label: 'Wix / Squarespace' },
    { id: 'wordpress', label: 'WordPress' },
    { id: 'custom', label: 'Custom-built' },
    { id: 'unsure', label: 'Not sure' },
];
export const SITE_ACCESS = [
    { id: 'yes', label: 'Yes' },
    { id: 'partly', label: 'Some of it' },
    { id: 'no', label: 'No' },
];
export const AGENT_CHANNELS = [
    { id: 'discord', label: 'Discord' },
    { id: 'slack', label: 'Slack' },
    { id: 'telegram', label: 'Telegram' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'web', label: 'A chat box on our site' },
    { id: 'other', label: 'Somewhere else' },
];
export const AGENT_TASKS = [
    { id: 'email', label: 'Read and sort our email' },
    { id: 'answer_docs', label: 'Answer from our files and docs' },
    { id: 'customer_replies', label: 'Reply to customers' },
    { id: 'scheduling', label: 'Book and move appointments' },
    { id: 'reports', label: 'Pull numbers and send summaries' },
    { id: 'data_entry', label: 'Move data between our tools' },
    { id: 'monitoring', label: 'Watch something and tell us when it changes' },
    { id: 'unsure', label: 'Not sure yet' },
];
export const AGENT_ACCESS = [
    { id: 'me', label: 'Just me' },
    { id: 'team', label: 'My team' },
    { id: 'customers', label: 'My customers' },
];
export const EVENT_TYPES = [
    { id: 'wedding', label: 'Wedding' },
    { id: 'debut', label: 'Debut' },
    { id: 'birthday', label: 'Birthday' },
    { id: 'christening', label: 'Christening' },
    { id: 'corporate', label: 'Corporate' },
    { id: 'reunion', label: 'Reunion' },
    { id: 'other', label: 'Other' },
];
export const GUEST_COUNTS = [
    { id: 'lt50', label: 'Under 50' },
    { id: '50-150', label: '50 – 150' },
    { id: '150-300', label: '150 – 300' },
    { id: '300+', label: '300+' },
];
export const RSVP_FEATURES = [
    { id: 'rsvp_meal', label: 'RSVP + meal choice' },
    { id: 'guest_codes', label: 'Guest codes' },
    { id: 'seating', label: 'Seating finder' },
    { id: 'story_deck', label: 'Story deck' },
    { id: 'gifts', label: 'Gift / GCash QR' },
    { id: 'photo_wall', label: 'Photo wall' },
    { id: 'checkin', label: 'QR check-in' },
    { id: 'livestream', label: 'Livestream page' },
];
export const RSVP_ADDRESSES = [
    { id: 'own_domain', label: 'Own domain (e.g. juanandmaria.com)' },
    { id: 'subdomain', label: 'A Codasc subdomain is fine' },
];
export const PERSONAL_TYPES = [
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'blog', label: 'Blog' },
    { id: 'resume', label: 'Resume' },
    { id: 'link', label: 'Link page' },
    { id: 'professional', label: 'Professional practice' },
];
export const BLOG_WRITERS = [
    { id: 'self', label: 'I will' },
    { id: 'us', label: 'Please post for me' },
];
// ─── Step 2: Logo Design ─────────────────────────────────────────────────
//
// The rules behind these lists are docs/studio/logo-service-rules.md. The
// prices below are the ones the visitor reads on step 2 (LOGO_RULES), so they
// live here once rather than in the copy.
/** Presentation deck add-on: up to 10 slides, then per slide. */
export const LOGO_DECK_CENTAVOS = 300_000;
export const LOGO_DECK_SLIDES = 10;
export const LOGO_DECK_EXTRA_SLIDE_CENTAVOS = 30_000;
export const LOGO_EXTRA_ROUND_CENTAVOS = 75_000;
export const LOGO_NEW_SET_CENTAVOS = 250_000;
export const LOGO_NEW_CONCEPT_CENTAVOS = 100_000;
/** Off the logo price (new, animate or redraw) when they let us show the logo in our portfolio. */
export const LOGO_PORTFOLIO_DISCOUNT_PERCENT = 10;
export const LOGO_NEEDS = [
    { id: 'new', label: 'A new logo' },
    { id: 'animate', label: 'Animate my existing logo' },
    { id: 'redraw', label: 'Redraw my existing logo (clean vector)' },
];
/** Paths that start from a logo the visitor already has, so we must see it. */
export function logoNeedsCurrent(need) {
    return need === 'animate' || need === 'redraw';
}
/**
 * Paths where we draw, so the flat-vector scope applies. Animating takes their
 * file as it is; what it needs instead is that the file is layered.
 */
export function logoNeedsScope(need) {
    return need === 'new' || need === 'redraw';
}
export const LOGO_STYLES = [
    { id: 'wordmark', label: 'Business name, designed' },
    { id: 'monogram', label: 'Initials' },
    { id: 'combination', label: 'Icon + name' },
    { id: 'icon', label: 'Simple icon' },
    { id: 'abstract', label: 'Abstract shape' },
    { id: 'mascot', label: 'Simple flat mascot' },
    { id: 'unsure', label: 'Not sure yet' },
];
export const LOGO_USES = [
    { id: 'social', label: 'Social media' },
    { id: 'signage', label: 'Signage / storefront' },
    { id: 'packaging', label: 'Packaging / labels' },
    { id: 'merch', label: 'Uniforms / merch' },
    { id: 'website', label: 'Website' },
    { id: 'video', label: 'Videos' },
];
export const LOGO_ADDONS = [
    {
        id: 'deck',
        label: `Presentation deck (+${formatPesos(LOGO_DECK_CENTAVOS)}, up to ${LOGO_DECK_SLIDES} slides)`,
    },
    { id: 'extra_animation', label: 'Another animation style' },
];
/**
 * The scope line, versioned like the consent line and for the same reason:
 * when someone later asks for a portrait logo, the lead says which wording
 * they agreed to. Bump the version whenever the text changes.
 */
export const LOGO_SCOPE_VERSION = '2026-10-04.1';
export const LOGO_SCOPE_TEXT = 'I understand logos are flat vector designs (no photos or realistic portraits).';
export const LOGO_LAYERED_TEXT = 'I have my logo as an SVG with each part on its own layer, not converted from a JPG or PNG.';
/** Optional, unlike the two above: unticked just means the full price. */
export const LOGO_PORTFOLIO_TEXT = `You may show my logo in your portfolio, and I get ${LOGO_PORTFOLIO_DISCOUNT_PERCENT}% off the logo price.`;
/**
 * The logo rules as the visitor reads them on step 2, from
 * docs/studio/logo-service-rules.md. `[heading, points]`, in reading order.
 *
 * Money stays out of step 2 everywhere else (studio-services.md §4c). Logo work
 * is the exception on purpose: the rounds and the fees for going past them ARE
 * the offer, and someone who meets them first in a quote feels misled.
 */
export const LOGO_RULES = [
    [
        'What you get',
        [
            '3–5 logo concepts, within 2–3 days of your brief.',
            '2 revision rounds on the one you pick, 1–3 days each.',
            'Final files: SVG, PNG and PDF.',
            'An animated logo with your business name, 3–5 seconds, in 16:9, 9:16 and 1:1, plus a transparent version for your own videos.',
            'A social media profile kit made from your logo.',
            'Not included: video intros.',
        ],
    ],
    [
        'What we design',
        [
            'Flat vector logos: your business name designed, initials, a simple icon, an abstract shape, an icon with your name, or a simple mascot built from shapes.',
            'Not: photos, realistic portraits or caricatures, painted, textured or 3D styles, or copies of another brand.',
            'Flat logos stay sharp at every size, print cleanly, and can be animated piece by piece.',
        ],
    ],
    [
        'Revisions',
        [
            'A round is one list of changes, sent all at once.',
            `Extra rounds are ${formatPesos(LOGO_EXTRA_ROUND_CENTAVOS)} each.`,
            `A different symbol or idea is a new concept, not a revision: ${formatPesos(LOGO_NEW_SET_CENTAVOS)} for a new set of 3, or ${formatPesos(LOGO_NEW_CONCEPT_CENTAVOS)} for one. At most 2 extra sets.`,
            'Switching to a new concept keeps the rounds you have left. It does not start over.',
        ],
    ],
    [
        'Approval',
        [
            'You approve the final logo in writing before we make the animation, the kit or the deck.',
            'Changing the logo after that is paid rework, because everything made from it is redone.',
            'No reply in 14 days and the latest version counts as approved, and we send your files.',
        ],
    ],
    [
        'Add-ons',
        [
            `Presentation deck: ${formatPesos(LOGO_DECK_CENTAVOS)} for up to ${LOGO_DECK_SLIDES} slides, then ${formatPesos(LOGO_DECK_EXTRA_SLIDE_CENTAVOS)} a slide. You give the text and images; 1 revision round. Hosted on codasc.dev/decks, with a PDF if you need one.`,
            'Animate your existing logo: you send an SVG with each part on its own layer. Quoted by complexity.',
            'Redraw your existing logo as a clean vector, if you only have a JPG or PNG. Quoted by complexity.',
        ],
    ],
    [
        'Ownership',
        [
            'The logo is yours once it is paid in full.',
            'Trademark search and registration are not included.',
            `Let us show your logo in our portfolio and the logo price is ${LOGO_PORTFOLIO_DISCOUNT_PERCENT}% less. It is up to you.`,
        ],
    ],
];
/**
 * The logo service page, `/studio/logo`. Its words live here because the web
 * build writes them into the page's HTML too (title, meta, the headline that
 * paints before the bundle, the structured data), and two copies drift.
 */
export const LOGO_PAGE = {
    /** Under the studio's base path: `/studio/logo`. */
    path: 'logo',
    /** Written for search: what someone types. */
    title: 'Logo design in the Philippines, from ₱5,000 — Codasc Studio',
    description: 'A flat vector logo for your business: 3–5 concepts in 2–3 days, 2 revision rounds, an animated logo for your videos, and a social media profile kit. Starts at ₱5,000.',
    /** Written for the share card: what makes someone tap. */
    ogTitle: 'A logo for your business — Codasc Studio',
    h1: 'A logo for your business.',
    dek: '3–5 concepts in 2–3 days, an animated version for your videos, and your social media profile kit.',
};
export const CHANNELS = [
    { id: 'messenger', label: 'Messenger' },
    { id: 'viber', label: 'Viber' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'email', label: 'Email' },
    { id: 'call', label: 'Call / SMS' },
];
/** Channels we reach through the mobile number, so it becomes required. */
export function channelNeedsMobile(channel) {
    return channel === 'viber' || channel === 'whatsapp' || channel === 'call';
}
export const HEARD_FROM = [
    { id: 'facebook', label: 'Facebook' },
    { id: 'google', label: 'Google' },
    { id: 'friend', label: 'A friend' },
    { id: 'other', label: 'Other' },
];
/**
 * The consent line, versioned. The version is stored on every lead so that if
 * the wording changes, each row still says which wording its person agreed to.
 * Bump the version whenever the text changes.
 *
 * It asks for the one thing the visitor is actually deciding: may we contact
 * you. What happens to the data afterwards — including the hashed email and
 * mobile sent to Meta to measure ads — is disclosed in the privacy and cookie
 * policies, linked beside the box, which is where that detail belongs and
 * where people expect to find it.
 */
export const CONSENT_VERSION = '2026-09-21.2';
export const CONSENT_TEXT = 'Codasc may contact me about this request.';
// ─── Normalizers (used by both sides, so the client never sends what the ───
// ─── server would refuse)                                                ───
/**
 * A phone number as E.164 (`+639171234567`), or null if it isn't one.
 *
 * Philippine mobiles are accepted in every shape people type them —
 * `0917 123 4567`, `9171234567`, `63917…`, `+63 917…` — because this form's
 * whole audience is in the Philippines and none of those should bounce. Any
 * other number must be written internationally (`+971…`), which is how a
 * relative abroad would give it anyway.
 */
export function normalizePhone(input) {
    if (!input)
        return null;
    const raw = input.trim();
    if (!raw)
        return null;
    // Only digits, spaces, dashes, dots, parentheses and one leading +.
    if (!/^\+?[\d\s().-]+$/.test(raw))
        return null;
    const digits = raw.replace(/\D/g, '');
    if (/^09\d{9}$/.test(digits))
        return `+63${digits.slice(1)}`;
    if (/^9\d{9}$/.test(digits))
        return `+63${digits}`;
    if (/^639\d{9}$/.test(digits))
        return `+${digits}`;
    if (raw.startsWith('+') && /^[1-9]\d{7,14}$/.test(digits))
        return `+${digits}`;
    return null;
}
/** Lower-cased, trimmed email, or null if it doesn't look like one. */
export function normalizeEmail(input) {
    if (!input)
        return null;
    const v = input.trim().toLowerCase();
    if (v.length > 200)
        return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : null;
}
/** Whole-peso label for a centavo price: 1_000_000 → "₱10,000". */
export function formatPesos(centavos) {
    return `₱${Math.round(centavos / 100).toLocaleString('en-PH')}`;
}
// ─── Page copy (also the source of the page's JSON-LD) ───────────────────
/**
 * The page's own copy, kept here rather than in the component because the
 * build also turns it into the page's structured data (see the studio web
 * vite.config.ts). Two copies of an answer is one copy that goes stale, and
 * a search result quoting an answer the page no longer gives is worse than
 * no structured data at all.
 */
export const HOW = [
    [
        'Pick what you need',
        'A new build, an upgrade to what you already have, an event site — or something else.',
    ],
    ['Tell us about it', 'A few taps and a sentence or two. No brief to write.'],
    ['Get a quote', 'We reply on the channel you chose, with questions or a price and a timeline.'],
    ['We build it', 'You review on your phone as it comes together.'],
];
/** How paying works. Also on the logo page, which has its own FAQ. */
export const PAYMENT_FAQ = [
    [
        'Does it cost anything to ask?',
        'No. Sending this form is free, and so is the quote you get back — neither commits you to anything. If you approve a quote, work starts on a downpayment of 20%, minimum ₱1,000, which counts towards the price rather than being charged on top.',
    ],
    [
        'Is the downpayment refundable?',
        'Once work has started, no — it pays for the slot we hold for you and the work already done. If we cannot take your project on, or we cancel before starting, you get it back in full.',
    ],
];
/** The logo answers: in the page's FAQ, and again on /studio/logo. */
export const LOGO_FAQ = [
    [
        'Can you put my photo or face in my logo?',
        'No. We design flat vector logos — your business name, initials, a simple icon, an abstract shape, or a simple mascot built from shapes. They stay sharp at every size and can be animated. Photos, realistic portraits and painted styles are not something we take on.',
    ],
    [
        'How many revisions does a logo include?',
        `Two rounds on the concept you pick, each one list of changes sent at once. Extra rounds are ${formatPesos(LOGO_EXTRA_ROUND_CENTAVOS)}. A completely different idea is a new concept rather than a revision, and is priced as one.`,
    ],
];
export const FAQ = [
    ...PAYMENT_FAQ,
    [
        'Can you work on a site I already have?',
        'Yes — that is what Web App Optimization is for. Send the link and say what bothers you: it loads slowly, it breaks on phones, it looks dated, nobody finds it on Google, or something simply stopped working. Starts at ₱10,000, quoted once we have looked at it.',
    ],
    [
        'How long does a project take?',
        'It depends on what you need. Your quote comes with a timeline.',
    ],
    ['Do I need an account?', 'No. Just tell us how to reach you.'],
    [
        'What’s mine, and what’s yours?',
        'Yours: the domain, in your name; everything you write or upload; and your data, exported whenever you ask — including if you leave. Ours: the code underneath, which we reuse across projects, and that is what keeps the price this low. You have the use of it for your project while your plan runs. If you want the code itself, say so and we will price it in — usually double the build fee. A logo is different: it is yours outright once it is paid in full.',
    ],
    ...LOGO_FAQ,
    [
        'What is the monthly fee on an AI agent for?',
        'Keeping it running: hosting, monitoring, and support when something needs attention. Support is included — you are never billed per question. Websites and apps have no monthly unless you ask us to look after them.',
    ],
    [
        'What happens after I send this?',
        'We read it, then reach you on the channel you picked — with questions if we need more, or a quote.',
    ],
];
//# sourceMappingURL=index.js.map